import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook for pagination logic
 * @param {number} totalItems - Total number of items
 * @param {number} itemsPerPage - Number of items per page
 * @param {Array} dependencies - Dependencies that should reset pagination
 * @returns {Object} - Pagination state and actions
 */
export const usePagination = (totalItems, itemsPerPage, dependencies = []) => {
  const [currentPage, setCurrentPage] = useState(0);

  // Calculate pagination values
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  // Reset to first page when dependencies change (e.g., filters, search)
  useEffect(() => {
    setCurrentPage(0);
  }, dependencies);

  // Handle page changes
  const handlePageChange = useCallback(newPage => {
    setCurrentPage(newPage);
  }, []);

  // Go to specific page
  const goToPage = useCallback(
    page => {
      if (page >= 0 && page < totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages]
  );

  // Go to first page
  const goToFirstPage = useCallback(() => {
    setCurrentPage(0);
  }, []);

  // Go to last page
  const goToLastPage = useCallback(() => {
    setCurrentPage(totalPages - 1);
  }, [totalPages]);

  // Go to next page
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  // Go to previous page
  const goToPrevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  // Check if we can navigate to next/previous
  const canGoNext = currentPage < totalPages - 1;
  const canGoPrev = currentPage > 0;

  return {
    // State
    currentPage,
    totalPages,
    startIndex,
    endIndex,

    // Actions
    setCurrentPage,
    handlePageChange,
    goToPage,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPrevPage,

    // Computed values
    canGoNext,
    canGoPrev,

    // Paginated items slice (helper for common use case)
    getPaginatedItems: items => items.slice(startIndex, endIndex),
  };
};
